import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { GraduationCap, Save, Loader2, Plus, Pencil, Trash2, ClipboardList, Search } from 'lucide-react';
import { useInstitution } from '../../contexts/InstitutionContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissao } from '../../hooks/usePermissao';
import { useToast, useConfirm } from '../../Components/Feedback';
import { Card, Select, Input, Button, Modal } from '../../Components/ui';
import AlunoBadges from '../../Components/AlunoBadges';
import { API_URL } from '../../api/config';
import { useApi } from '../../api/useApi';

// Tela de lançamento de notas por período avaliativo (bimestre/trimestre) e
// por CATEGORIA pedagógica (Educacional/Esporte/Cultura-Teoria/Cultura-
// Prática/Dança — ver backend/categorias-avaliativas.js), não por turma
// exata: um aluno com 2 turmas de instrumento tem uma nota SÓ de "Cultura/
// Prática", lançada direto por qualquer professor dele nessa categoria (sem
// exclusividade entre professores da mesma categoria). Tela própria,
// separada da Grade (que é só visualização de horário), no mesmo espírito
// de "Formação de Caráter".
//
// Média do período = (soma das notas das categorias em que o aluno participa
// + % de frequência) ÷ (quantidade dessas categorias + 1) — só fecha quando
// TODAS as categorias reais do aluno (não só as que o professor logado
// ensina) já têm nota. Frequência é a GERAL do aluno no intervalo do período
// (não por turma — `presenca` não guarda qual turma o aluno frequentou
// naquele dia, só se esteve presente na instituição; ver comentário em
// calcularFrequenciaPorAluno, backend/relatorios.js).
const normalizar = (s) =>
  String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

function Notas() {
  const { institutionId } = useInstitution();
  const { token, user } = useAuth();
  const { podeCriar, podeEditar, podeExcluir } = usePermissao('/notas');
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  const authHeaders = useMemo(
    () => ({
      'x-institution-id': institutionId,
      Authorization: `Bearer ${token}`,
    }),
    [institutionId, token],
  );
  const api = useApi();

  const podeGerenciarPeriodos = ['master', 'coordenador'].includes(user?.perfil);
  const souProfessor = user?.perfil === 'professor';
  const souProfessorSemVinculo = souProfessor && !user?.id_professor;

  const [idPeriodo, setIdPeriodo] = useState('');
  const [idProfessorFiltro, setIdProfessorFiltro] = useState(''); // só master/coordenador — filtra o seletor de Turma abaixo por professor escolhido
  const [idAtividade, setIdAtividade] = useState(''); // professor: sempre a turma dele; master/coordenador: só depois de escolher um professor acima
  const [alteracoes, setAlteracoes] = useState({}); // "alunoId|categoria" -> valor (string)
  const [salvando, setSalvando] = useState(false);
  const [isGerenciarPeriodosOpen, setIsGerenciarPeriodosOpen] = useState(false);
  const [busca, setBusca] = useState('');

  const { data: periodos = [], isLoading: carregandoPeriodos } = useQuery({
    queryKey: ['notas-periodos', institutionId],
    queryFn: async () => {
      const res = await api.get(`/api/notas/periodos`);
      return res?.data ?? (Array.isArray(res) ? res : []);
    },
    enabled: !!institutionId && !!token,
  });

  // Lista de professores — só master/coordenador vê esse seletor, pra poder
  // escolher "de qual professor" quer ver as turmas (pedido explícito: "poder
  // selecionar por professores e turmas de cada professor").
  const { data: professores = [] } = useQuery({
    queryKey: ['notas-professores', institutionId],
    queryFn: async () => {
      const res = await api.get(`/api/notas/professores`);
      return res?.data ?? (Array.isArray(res) ? res : []);
    },
    enabled: !!institutionId && !!token && podeGerenciarPeriodos,
  });

  // Turma: pro professor é sempre "as turmas dele" (lança nota "por turma
  // dele" — pedido explícito, pra não virar uma lista solta de todo aluno que
  // ele dá aula em qualquer categoria/turma, sem separar). Master/coordenador
  // só busca turmas depois de escolher um professor acima — sem professor
  // escolhido continua vendo todo mundo direto, sem filtro (comportamento
  // padrão de sempre).
  const { data: turmas = [] } = useQuery({
    queryKey: ['notas-turmas', institutionId, idProfessorFiltro],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (idProfessorFiltro) params.set('professor_id', idProfessorFiltro);
      const res = await fetch(`${API_URL}/api/notas/turmas?${params}`, { headers: authHeaders });
      if (!res.ok) throw new Error('Erro ao carregar turmas');
      const body = await res.json();
      return body?.data ?? (Array.isArray(body) ? body : []);
    },
    enabled:
      !!institutionId &&
      !!token &&
      (souProfessor || (podeGerenciarPeriodos && !!idProfessorFiltro)),
  });

  const { data: dadosNotas, isLoading: carregandoNotas } = useQuery({
    queryKey: ['notas-alunos', institutionId, idPeriodo, idAtividade],
    queryFn: async () => {
      const params = new URLSearchParams({ id_periodo: idPeriodo });
      if (idAtividade) params.set('id_atividade', idAtividade);
      const res = await fetch(`${API_URL}/api/notas/alunos?${params}`, { headers: authHeaders });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'Erro ao carregar notas');
      return body?.data ?? body;
    },
    enabled:
      !!institutionId &&
      !!token &&
      !!idPeriodo &&
      (!souProfessor || !!idAtividade) &&
      (!idProfessorFiltro || !!idAtividade),
  });

  const categorias = dadosNotas?.categorias ?? [];
  const alunos = dadosNotas?.alunos ?? [];

  const buscaNorm = normalizar(busca.trim());
  const alunosFiltrados = useMemo(() => {
    if (!buscaNorm) return alunos;
    return alunos.filter((a) => normalizar(a.nome).includes(buscaNorm));
  }, [alunos, buscaNorm]);

  const handleAlterarNota = (alunoId, categoria, valor) => {
    setAlteracoes((prev) => ({ ...prev, [`${alunoId}|${categoria}`]: valor }));
  };

  const valorExibido = (aluno, categoria) => {
    const chave = `${aluno.aluno_id}|${categoria}`;
    if (alteracoes[chave] !== undefined) return alteracoes[chave];
    const notaExistente = aluno.notas?.[categoria];
    if (notaExistente !== null && notaExistente !== undefined && notaExistente !== '') {
      return String(notaExistente);
    }
    const temAtividade = aluno.matriculas_categorias
      ? aluno.matriculas_categorias.includes(categoria)
      : false;
    return temAtividade ? '' : '—';
  };

  const handleSalvar = async () => {
    const notasParaEnviar = Object.entries(alteracoes).map(([chave, valor]) => {
      const [alunoId, categoria] = chave.split('|');
      const notaValor = valor === '—' || valor === '-' ? '' : valor;
      return { id_aluno: Number(alunoId), categoria, nota: notaValor };
    });
    if (notasParaEnviar.length === 0) {
      toast.info('Nenhuma alteração para salvar.');
      return;
    }
    setSalvando(true);
    try {
      const res = await fetch(`${API_URL}/api/notas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ id_periodo: idPeriodo, notas: notasParaEnviar }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao salvar notas');
      const total = data?.data?.total ?? data?.total ?? notasParaEnviar.length;
      toast.success(data?.message || `${total} nota(s) salva(s) com sucesso!`);
      setAlteracoes({});
      queryClient.invalidateQueries({ queryKey: ['notas-alunos', institutionId, idPeriodo] });
    } catch (error) {
      toast.error('Erro ao salvar notas: ' + error.message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950">
      <main className="p-4 max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Lançamento de Notas
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Nota por categoria (Educacional, Esporte, Cultura e Dança) + frequência, por período
              avaliativo
            </p>
          </div>
          {podeGerenciarPeriodos && (
            <Button
              variant="secondary"
              icon={ClipboardList}
              onClick={() => setIsGerenciarPeriodosOpen(true)}
            >
              Gerenciar Períodos
            </Button>
          )}
        </div>

        {souProfessorSemVinculo ? (
          <Card>
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <GraduationCap className="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-gray-700" />
              <p className="text-sm">
                Sua conta ainda não está vinculada a um cadastro de educador.
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                Peça pro master vincular em Admin Usuários.
              </p>
            </div>
          </Card>
        ) : (
          <>
            <Card>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <Select
                  label="Período Avaliativo"
                  value={idPeriodo}
                  onChange={(e) => {
                    setIdPeriodo(e.target.value);
                    setAlteracoes({});
                  }}
                  options={[
                    {
                      value: '',
                      label: carregandoPeriodos
                        ? 'Carregando...'
                        : periodos.length === 0
                          ? 'Nenhum período criado ainda'
                          : 'Selecione...',
                    },
                    ...periodos.map((p) => ({ value: p.id, label: p.nome })),
                  ]}
                />
                {podeGerenciarPeriodos && (
                  <Select
                    label="Professor"
                    value={idProfessorFiltro}
                    onChange={(e) => {
                      setIdProfessorFiltro(e.target.value);
                      setIdAtividade('');
                      setAlteracoes({});
                    }}
                    options={[
                      { value: '', label: 'Todos (sem filtro)' },
                      ...professores.map((p) => ({ value: p.id, label: p.nome })),
                    ]}
                  />
                )}
                {(souProfessor || (podeGerenciarPeriodos && !!idProfessorFiltro)) && (
                  <Select
                    label="Turma"
                    value={idAtividade}
                    onChange={(e) => {
                      setIdAtividade(e.target.value);
                      setAlteracoes({});
                    }}
                    options={[
                      { value: '', label: turmas.length === 0 ? 'Nenhuma turma' : 'Selecione...' },
                      ...turmas.map((t) => ({
                        value: t.id,
                        label: `${t.nome} — ${t.dia_semana} ${t.horario} (${t.turno})`,
                      })),
                    ]}
                  />
                )}
                <Input
                  label="Pesquisar Inscrito"
                  placeholder="Buscar aluno por nome..."
                  icon={Search}
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                />
              </div>
              {!carregandoPeriodos && periodos.length === 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-3">
                  {podeGerenciarPeriodos
                    ? 'Nenhum período avaliativo cadastrado ainda — clique em "Gerenciar Períodos" acima para criar o primeiro.'
                    : 'Nenhum período avaliativo foi cadastrado ainda. Peça pra coordenação criar um em "Gerenciar Períodos".'}
                </p>
              )}
            </Card>

            {!idPeriodo || (souProfessor && !idAtividade) || (idProfessorFiltro && !idAtividade) ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <GraduationCap className="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-gray-700" />
                <p className="text-sm">
                  {!idPeriodo ? 'Selecione um período avaliativo' : 'Selecione uma turma'} para
                  lançar as notas.
                </p>
              </div>
            ) : carregandoNotas ? (
              <div className="flex items-center justify-center py-16 text-gray-500 dark:text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin mr-3" /> Carregando...
              </div>
            ) : (
              <Card padding="none">
                <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between flex-wrap gap-2">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {buscaNorm
                      ? `${alunosFiltrados.length} de ${alunos.length} inscrito${alunos.length === 1 ? '' : 's'}`
                      : `${alunos.length} inscrito${alunos.length === 1 ? '' : 's'}`}
                  </h3>
                  <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                    {podeEditar && (
                      <Button
                        icon={salvando ? undefined : Save}
                        onClick={handleSalvar}
                        disabled={salvando || Object.keys(alteracoes).length === 0}
                      >
                        {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                        {salvando
                          ? 'Salvando...'
                          : `Salvar Alterações (${Object.keys(alteracoes).length})`}
                      </Button>
                    )}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                          Inscrito
                        </th>
                        {categorias.map((cat) => (
                          <th
                            key={cat.chave}
                            className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400"
                          >
                            {cat.label}
                          </th>
                        ))}
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400">
                          % Frequência
                        </th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400">
                          Média
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {alunos.length === 0 ? (
                        <tr>
                          <td
                            colSpan={categorias.length + 3}
                            className="px-4 py-10 text-center text-gray-400 dark:text-gray-500"
                          >
                            Nenhum inscrito encontrado pra esse período.
                          </td>
                        </tr>
                      ) : alunosFiltrados.length === 0 ? (
                        <tr>
                          <td
                            colSpan={categorias.length + 3}
                            className="px-4 py-10 text-center text-gray-400 dark:text-gray-500"
                          >
                            Nenhum aluno encontrado para a busca &ldquo;{busca}&rdquo;.
                          </td>
                        </tr>
                      ) : (
                        alunosFiltrados.map((aluno) => (
                          <tr
                            key={aluno.aluno_id}
                            className="border-b border-gray-50 dark:border-gray-800 last:border-0"
                          >
                            <td
                              className="px-4 py-2 text-gray-900 dark:text-gray-100"
                              translate="no"
                            >
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span>{aluno.nome}</span>
                                <AlunoBadges aluno={aluno} compact />
                              </div>
                            </td>
                            {categorias.map((cat) => {
                              const valor = valorExibido(aluno, cat.chave);
                              const temAtividade = aluno.matriculas_categorias
                                ? aluno.matriculas_categorias.includes(cat.chave)
                                : true;
                              const ehTracoSemAtividade = valor === '—' || valor === '-';

                              return (
                                <td key={cat.chave} className="px-2 py-1.5 text-center">
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    value={valor}
                                    onFocus={(e) => {
                                      if (e.target.value === '—' || e.target.value === '-') {
                                        e.target.select();
                                      }
                                    }}
                                    onChange={(e) =>
                                      handleAlterarNota(aluno.aluno_id, cat.chave, e.target.value)
                                    }
                                    disabled={!podeEditar}
                                    placeholder={!temAtividade ? '—' : ''}
                                    className={`w-20 h-8 text-center rounded-lg border text-sm transition-all focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                                      ehTracoSemAtividade
                                        ? 'border-dashed border-gray-300 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40 text-gray-400 dark:text-gray-500'
                                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-medium'
                                    }`}
                                    title={
                                      ehTracoSemAtividade
                                        ? 'Sem atividade cadastrada nesta área. Apague o traço para lançar nota.'
                                        : undefined
                                    }
                                  />
                                </td>
                              );
                            })}
                            <td className="px-4 py-2 text-center text-gray-600 dark:text-gray-400">
                              {aluno.frequencia_pct}%
                            </td>
                            <td className="px-4 py-2 text-center font-semibold text-gray-900 dark:text-gray-100">
                              {aluno.media !== null ? aluno.media : '—'}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </>
        )}
      </main>

      {podeGerenciarPeriodos && (
        <GerenciarPeriodosModal
          isOpen={isGerenciarPeriodosOpen}
          onClose={() => setIsGerenciarPeriodosOpen(false)}
          periodos={periodos}
          authHeaders={authHeaders}
          institutionId={institutionId}
          queryClient={queryClient}
          toast={toast}
          confirm={confirm}
          podeCriar={podeCriar}
          podeEditar={podeEditar}
          podeExcluir={podeExcluir}
        />
      )}
    </div>
  );
}

// Modal simples de CRUD de períodos avaliativos (master/coordenador) — nome
// livre + intervalo de datas, sem forçar "bimestre"/"trimestre" como opção
// fixa (cada instituição usa o esquema que quiser).
function GerenciarPeriodosModal({
  isOpen,
  onClose,
  periodos,
  authHeaders,
  institutionId,
  queryClient,
  toast,
  confirm,
  podeCriar,
  podeEditar,
  podeExcluir,
}) {
  const api = useApi();
  const [editando, setEditando] = useState(null); // null = nenhum form aberto; {} = criando; {id,...} = editando
  const [form, setForm] = useState({ nome: '', data_inicio: '', data_fim: '' });
  const [salvando, setSalvando] = useState(false);

  const abrirNovo = () => {
    setForm({ nome: '', data_inicio: '', data_fim: '' });
    setEditando({});
  };
  const abrirEdicao = (p) => {
    setForm({ nome: p.nome, data_inicio: p.data_inicio, data_fim: p.data_fim });
    setEditando(p);
  };

  const salvar = async () => {
    if (!form.nome.trim() || !form.data_inicio || !form.data_fim) {
      toast.warning('Preencha nome, data de início e data de fim.');
      return;
    }
    setSalvando(true);
    try {
      const isEdicao = !!editando?.id;
      const url = isEdicao
        ? `${API_URL}/api/notas/periodos/${editando.id}`
        : `${API_URL}/api/notas/periodos`;
      const res = await fetch(url, {
        method: isEdicao ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao salvar período');
      toast.success(data?.message || (isEdicao ? 'Período atualizado.' : 'Período criado.'));
      setEditando(null);
      queryClient.invalidateQueries({ queryKey: ['notas-periodos', institutionId] });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (p) => {
    if (
      !(await confirm(
        `Excluir "${p.nome}"? Todas as notas lançadas nesse período também serão apagadas.`,
        { title: 'Excluir período', confirmText: 'Excluir' },
      ))
    )
      return;
    try {
      const res = await api.del(`/api/notas/periodos/${p.id}`);
      toast.success(res?.message || 'Período excluído.');
      queryClient.invalidateQueries({ queryKey: ['notas-periodos', institutionId] });
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Períodos Avaliativos" maxWidth="md">
      <div className="space-y-3">
        {editando ? (
          <div className="space-y-3 p-3 rounded-2xl bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-800">
            <Input
              label="Nome"
              placeholder="Ex.: 1º Bimestre 2026"
              value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
            />
            <div className="grid grid-cols-2 gap-3">
              <Input
                type="date"
                label="Data Início"
                value={form.data_inicio}
                onChange={(e) => setForm({ ...form, data_inicio: e.target.value })}
              />
              <Input
                type="date"
                label="Data Fim"
                value={form.data_fim}
                onChange={(e) => setForm({ ...form, data_fim: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setEditando(null)}>
                Cancelar
              </Button>
              <Button onClick={salvar} disabled={salvando}>
                {salvando ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        ) : podeCriar ? (
          <Button icon={Plus} onClick={abrirNovo} className="w-full">
            Novo Período
          </Button>
        ) : null}

        <div className="space-y-1.5 max-h-72 overflow-y-auto">
          {periodos.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6">
              Nenhum período avaliativo criado ainda.
            </p>
          ) : (
            periodos.map((p) => (
              <div
                key={p.id}
                className="flex items-center justify-between px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-800"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{p.nome}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {p.data_inicio.split('-').reverse().join('/')} —{' '}
                    {p.data_fim.split('-').reverse().join('/')}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {podeEditar && (
                    <button
                      onClick={() => abrirEdicao(p)}
                      className="w-8 h-8 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {podeExcluir && (
                    <button
                      onClick={() => excluir(p)}
                      className="w-8 h-8 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 flex items-center justify-center text-red-500 dark:text-red-400"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}

export default Notas;
